import { httpClient } from '../http-client'
import { filterStructureForStudent } from '@/lib/utils/assigned-visibility'
import type {
  ProductStructureParams,
  ProductStructureResponse,
  ProductStructureDto,
  LearningPathDto,
  ModuleDto,
  StepDto
} from '@/types/api'

/**
 * ProductToolsService - Gerencia estrutura e ferramentas de produtos
 */
class ProductToolsService {
  /**
   * Valida o ID do produto
   */
  private validateProductId(productId: string): void {
    if (!productId || typeof productId !== 'string' || productId.trim().length === 0) {
      throw new Error('Valid product ID is required')
    }
  }

  /**
   * Valida os parâmetros da requisição
   */
  private validateParams(params?: ProductStructureParams): void {
    if (!params) return

    if (params.type && !['tracks', 'modules', 'steps'].includes(params.type)) {
      throw new Error(`Invalid type: ${params.type}. Must be 'tracks', 'modules', or 'steps'`)
    }

    if (params.trackId && params.moduleId) {
      throw new Error('Cannot specify both trackId and moduleId simultaneously')
    }
  }

  /**
   * Trata erros da API de forma específica
   */
  private handleApiError(error: any, productId: string): never {
    if (error.response?.status === 404) {
      throw new Error(`Product with ID ${productId} not found`)
    }
    if (error.response?.status === 403) {
      throw new Error(`Access denied to product ${productId}`)
    }
    throw new Error(`Failed to fetch product structure: ${error.message}`)
  }

  /**
   * Valida a resposta da API
   */
  private validateResponse(data: any): ProductStructureResponse {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format')
    }

    // Se o acesso foi negado, o backend retorna product: null com access_denied_reason
    if (!data.product && data.access_denied_reason) {
      return data as ProductStructureResponse
    }

    // Valida estrutura mínima esperada
    if (!data.product || typeof data.product !== 'object') {
      throw new Error('Response must contain product information')
    }

    if (!data.product.id || !data.product.title) {
      throw new Error('Product must have id and title')
    }

    return data as ProductStructureResponse
  }

  /**
   * Obtém a estrutura completa de um produto com suporte a diversas variações
   */
  async getProductStructure(
    productId: string,
    profileId: string,
    params?: ProductStructureParams
  ): Promise<ProductStructureResponse> {
    this.validateProductId(productId)
    this.validateParams(params)

    const fetchWithCache = async (skipCache: boolean) => {
      const response = await httpClient.post(
        `/products/${productId}/structure`,
        {
          ...params,
          skipCache,
          profileId
        }
      )
      return filterStructureForStudent(this.validateResponse(response.data), profileId)
    }

    try {
      return await fetchWithCache(false)
    } catch (error) {
      console.error(`Failed to fetch product structure for product ${productId}, retrying without cache:`, error)
      try {
        return await fetchWithCache(true)
      } catch (retryError) {
        this.handleApiError(retryError, productId)
      }
    }
  }

  // Métodos de conveniência para as variações mais comuns

  /**
   * Obtém estrutura completa do produto (equivalente ao GET)
   */
  async getFullProductStructure(productId: string, profileId: string): Promise<ProductStructureResponse> {
    return this.getProductStructure(productId, profileId)
  }

  /**
   * Obtém informações básicas do produto
   */
  async getProductInfo(productId: string, profileId: string): Promise<ProductStructureDto> {
    const response = await this.getProductStructure(productId, profileId)
    return response.product
  }

  /**
   * Obtém apenas learning paths (tracks) do produto
   */
  async getLearningPaths(productId: string, profileId: string, simplified = false): Promise<LearningPathDto[]> {
    const response = await this.getProductStructure(productId, profileId, { type: 'tracks', simplified })
    return response.learning_paths || []
  }

  /**
   * Obtém apenas módulos do produto
   */
  async getProductModules(
    productId: string,
    profileId: string,
    simplified = false,
    options?: { skipCache?: boolean },
  ): Promise<ModuleDto[]> {
    const response = await this.getProductStructure(productId, profileId, {
      type: 'modules',
      simplified,
      skipCache: options?.skipCache,
    })
    return response.modules || []
  }

  /**
   * Obtém apenas etapas do produto
   */
  async getProductSteps(productId: string, profileId: string, simplified = false): Promise<StepDto[]> {
    const response = await this.getProductStructure(productId, profileId, { type: 'steps', simplified })
    return response.steps || []
  }

  /**
   * Obtém estrutura de uma track específica
   */
  async getTrackStructure(
    productId: string,
    trackId: string,
    profileId: string,
    simplified = false
  ): Promise<ProductStructureResponse> {
    return this.getProductStructure(productId, profileId, { trackId, simplified })
  }

  /**
   * Obtém etapas de um módulo específico
   */
  async getModuleSteps(
    productId: string,
    moduleId: string,
    profileId: string,
    simplified = false
  ): Promise<ProductStructureResponse> {
    return this.getProductStructure(productId, profileId, { moduleId, simplified })
  }

  /**
   * Obtém versão simplificada de toda a estrutura
   */
  async getSimplifiedProductStructure(productId: string, profileId: string): Promise<ProductStructureResponse> {
    return this.getProductStructure(productId, profileId, { simplified: true })
  }

  /**
   * Obtém learning paths simplificados
   */
  async getSimplifiedLearningPaths(productId: string, profileId: string): Promise<LearningPathDto[]> {
    const response = await this.getProductStructure(productId, profileId, { type: 'tracks', simplified: true })
    return response.learning_paths || []
  }

  /**
   * Obtém estrutura de uma track específica (versão simplificada)
   */
  async getSimplifiedTrackStructure(
    productId: string,
    trackId: string,
    profileId: string
  ): Promise<ProductStructureResponse> {
    return this.getProductStructure(productId, profileId, { trackId, simplified: true })
  }

  /**
   * Obtém etapas de um módulo específico (versão simplificada)
   */
  async getSimplifiedModuleSteps(
    productId: string,
    moduleId: string,
    profileId: string
  ): Promise<ProductStructureResponse> {
    return this.getProductStructure(productId, profileId, { moduleId, simplified: true })
  }
}

export const productToolsService = new ProductToolsService()
